import Core                       from '@/core/Core';
import TransportRoute             from '@/domains/TransportRoutes/TransportRoute';
import TransportRoutesDAO         from '@/domains/TransportRoutes/TransportRoutesDAO';
import ContextMenuItem            from '@/lib/components/VContextMenu/ContextMenuItem';

import type RpcDataPagination     from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort           from '@/core/services/Rpc/RpcDataSort';

/**
 * Сервис для работы с маршрутами доставки
 */
class TransportRoutesService {

  /**
   * Загружает маршрут по ID
   */
  static async get(id: number): Promise<TransportRoute | null> {
    const response = await TransportRoutesDAO.get(id);

    if (!response.data) return null;

    return TransportRoute.fromServer(response.data);
  }

  /**
   * Загружает маршрут по паре зон (отправления и назначения).
   * Возвращает null если маршрут между этими зонами не задан.
   * Не фильтрует удалённые маршруты — потребитель сам решает что делать с isDeleted.
   */
  static async getByZones(fromId: number, toId: number): Promise<TransportRoute | null> {
    const response = await TransportRoutesDAO.getByZones(fromId, toId);

    if (!response.data) return null;

    return TransportRoute.fromServer(response.data);
  }

  /**
   * Загружает список маршрутов с пагинацией.
   * Используется в реестре через DataGridLoader.
   */
  static async getListPaginated(
    params?: Parameters<typeof TransportRoutesDAO.getList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: TransportRoute[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await TransportRoutesDAO.getList(filters, pagination, sort);

    return {
      data: Core.utils.hydrateArray(response.data, TransportRoute.fromServer),
      pagination: response.meta.pagination,
    };
  }

  /**
   * Загружает список маршрутов.
   * Для использования вне реестра.
   */
  static async getList(): Promise<TransportRoute[]> {
    return (await this.getListPaginated()).data;
  }

  /**
   * Создаёт маршрут
   */
  static async add(transportRoute: TransportRoute): Promise<boolean> {
    const errors = transportRoute.validate();
    if (errors.length > 0) {
      Core.showNotification.error(errors.join('\n'));
      return false;
    }

    const response = await TransportRoutesDAO.add(transportRoute);

    if (response.ok) {
      Core.showNotification.success('Маршрут добавлен');
    }

    return response.ok;
  }

  /**
   * Обновляет маршрут
   */
  static async update(transportRoute: TransportRoute): Promise<boolean> {
    const errors = transportRoute.validate();
    if (errors.length > 0) {
      Core.showNotification.error(errors.join('\n'));
      return false;
    }

    const response = await TransportRoutesDAO.update(transportRoute);

    if (response.ok) {
      Core.showNotification.success('Маршрут обновлён');
    }

    return response.ok;
  }

  /**
   * Удаляет маршрут
   */
  static async delete(id: number): Promise<boolean> {
    const response = await TransportRoutesDAO.delete(id);

    if (response.ok) {
      Core.showNotification.success('Маршрут удалён');
    }

    return response.ok;
  }

  /**
   * Удаляет маршрут с подтверждением.
   * Принимает сущность целиком — нужно её имя для текста подтверждения.
   */
  static async deleteWithConfirmation(transportRoute: TransportRoute): Promise<boolean> {
    const confirmed = await Core.dialogs.confirm(
      'Удалить этот маршрут?',
      `Маршрут ${transportRoute.name}`,
    );

    if (confirmed === true) {
      return await this.delete(transportRoute.id);
    } else {
      return false;
    }
  }

  /**
   * Открыть карточку маршрута.
   * Промис резолвится по закрытию карточки: true — сохранили, false — закрыли.
   */
  static showCard(transportRoute: TransportRoute | null): Promise<boolean> {
    return Core.dialogs.TransportRouteCard.showAsPromise({ id: transportRoute?.id });
  }

  // ============================================================
  // Контекстное меню
  // ============================================================

  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (route: TransportRoute) => { return this.showCard(route); },
        null,
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (route: TransportRoute) => { return this.deleteWithConfirmation(route); },
      ),
    ];
  }
}

export default TransportRoutesService;